/* eslint-disable no-empty-pattern */
import {SpecialCategories} from './uploads';
import {Employee} from '../models/employee';

import Vue from 'vue';
import ErrorCodes from '@/helpers/errorCodes';

const state = () => ({
   employees: {},
   uploadCategoryId: null,
});

const getters = {
   employees: (state) => Object.values(state.employees),
   employeeMap: (state) => state.employees,

   uploadCategory: (state, getters, rootState, rootGetters) => {
      if (state.uploadCategoryId === null) {
         return null;
      }
      return rootGetters['uploads/uploadCategoryMap'][state.uploadCategoryId];
   },
};

const mutations = {
   clear(state) {
      state.employees = {};
   },

   setEmployees(state, {employees}) {
      employees.forEach((employee) => {
         Vue.set(state.employees, employee.id, employee);
      });
   },

   updateEmployee(state, {employee}) {
      Vue.set(state.employees, employee.id, employee);
   },

   /** Remove an employee from state */
   deleteEmployee(state, {employeeId}) {
      if (employeeId in state.employees) {
         Vue.delete(state.employees, employeeId);
      }
   },

   /** Remove an employee from state */
   deleteCustomerEmployee(state, {employeeId}) {
      if (employeeId in state.employees) {
         Vue.delete(state.employees, employeeId);
      }
   },

   /** Store the UploadCategory ID for the Employees upload category */
   setUploadCategoryId(state, {uploadCategoryId}) {
      state.uploadCategoryId = uploadCategoryId;
   },
};

const actions = {
   /**
    * Fetch the employees of a company
    * @param {String} companyId - ID of the company
    * @param {string|number} [year] - Fetch employees from a given year
    * @param {Boolean} [allStudies] - Fetch employees from all studies
    */
   async fetchEmployees({}, {companyId, periodId = null, allStudies = false}) {
      const params = {company_id: companyId, include_closed_studies: allStudies};
      if (periodId !== null) {
         params.speriod_id = periodId;
      }

      const employees = (await this._vm.$http.get('/api/employees/vendor', {params})).data.results;
      return employees;
   },

   /**
    * Load the employees of a company
    * @param {String} companyId - ID of the company
    * @param {string|number} [year] - Fetch employees from a given year
    * @param {Boolean} [allStudies] - Fetch employees from all studies
    */
   async loadEmployees({commit, dispatch}, {companyId, periodId = null, allStudies = false}) {
      const employeeData = await dispatch('fetchEmployees', {
         companyId,
         periodId,
         allStudies,
      });
      const employees = employeeData.map((data) => new Employee(data));
      commit('clear');
      commit('setEmployees', {employees});
   },

   /**
    * Create a new employee
    * @param {string} companyId - Company to create the employee on
    * @param {Object} employee - The employee object
    * @param {string|number} year - The year to create the employee in
    */
   async createEmployee({commit, rootGetters}, {employee}) {
      const employeeData = (await this._vm.$http.post('/api/employees/vendor', employee.export()))
         .data;
      const newEmployee = new Employee(employeeData);
      commit('updateEmployee', {employee: newEmployee});

      const speriod = rootGetters['companies/studyPeriodMap'][employee.periodId];
      if (speriod) {
         await this._vm.$http.post('/api/employees/summary', {
            companyId: employee.companyId,
            periods: [
               {
                  companyId: employee.companyId,
                  employeeId: newEmployee.id,
                  period: speriod.period,
                  location: employee.location,
                  totalDebit: employee.payroll,
                  totalCredit: 0,
                  percentage: null,
                  projectsData: {},
                  activitiesData: {},
               },
            ],
         });
         // the vendor POST response has no payroll (Employee no longer has that
         // column), so backfill it here now that the summary row exists --
         // otherwise the row shows blank until the next page load re-merges it.
         newEmployee.payroll = employee.payroll;
         commit('updateEmployee', {employee: newEmployee});
      }
   },

   /**
    * Edit an employee's info
    * @param {Object} employee - The updated employee object
    */
   async editEmployee({commit, rootGetters}, {employee}) {
      const employeeData = (
         await this._vm.$http.put(`/api/employees/vendor/${employee.id}`, employee.export())
      ).data;
      const updatedEmployee = new Employee(employeeData);
      // the vendor PUT response has no payroll (Employee no longer has that
      // column) -- carry the edited value through until the summary write below
      // updates the real EmployeeExpenseSummary.total_debit.
      updatedEmployee.payroll = employee.payroll;
      commit('updateEmployee', {employee: updatedEmployee});

      const speriod = rootGetters['companies/studyPeriodMap'][employee.periodId];
      if (!speriod) {
         return;
      }

      const summaries = (
         await this._vm.$http.get('/api/employees/summary', {
            params: {company_id: employee.companyId},
         })
      ).data.results.results;
      const existing = summaries.find(
         (s) =>
            s.employeeId === employee.id &&
            s.period?.lower === speriod.period.lower &&
            s.period?.upper === speriod.period.upper
      );

      if (existing) {
         // partial update -- preserves percentage/projectsData/activitiesData
         // that the bulk upsert below would otherwise overwrite
         await this._vm.$http.put('/api/employees/summary/payment', {
            summaryId: existing.id,
            payment: employee.payroll,
            assigneeId: existing.assigneeId ?? null,
         });
      } else {
         await this._vm.$http.post('/api/employees/summary', {
            companyId: employee.companyId,
            periods: [
               {
                  companyId: employee.companyId,
                  employeeId: employee.id,
                  period: speriod.period,
                  location: employee.location,
                  totalDebit: employee.payroll,
                  totalCredit: 0,
                  percentage: null,
                  projectsData: {},
                  activitiesData: {},
               },
            ],
         });
      }
   },

   /**
    * Delete an employee.
    *
    * The data will only be deleted if the force flag is set to `true`.
    * Otherwise, the server will return a related data object describing related data
    * that would be deleted if this employee were deleted.
    * @param {string} employeeId - The employee's ID
    * @param {boolean} force - Force the deletion to be carried out
    */
   async deleteEmployee({commit}, {employeeId, force = false}) {
      const params = {force};
      try {
         await this._vm.$http.delete(`/api/employees/vendor/${employeeId}`, {params});
      } catch (err) {
         const errCode = err.response.data.errors[0].code;
         if (errCode === ErrorCodes.CONFIRMATION_REQUIRED) {
            return err.response.data.errors[0].detail;
         }
         throw err;
      }
      commit('deleteEmployee', {employeeId});
   },

   async deleteCustomerEmployee({commit}, {employeeId, force = false}) {
      const params = {force};
      let errCode = '';

      try {
         await this._vm.$http.delete(`/api/employees/vendor/${employeeId}`, {params});
         commit('deleteCustomerEmployee', {employeeId});
      } catch (err) {
         errCode = err.response.data.errors[0];
      }
      return errCode;
   },

   /**
    * Import a list of employees from a CSV file
    * @param {string} companyId - The ID of the company to import employees to
    * @param {string|number} year - The year to import the employees to
    * @param {string} file - The file to import employees from
    */
   async importEmployeeCsv({state, dispatch}, {companyId, periodId, file, force = false}) {
      const validation = {
         type: SpecialCategories.EMPLOYEES,
         data: {force},
      };
      await dispatch(
         'uploads/uploadFiles',
         {
            uploadCategoryId: state.uploadCategoryId,
            periodId,
            files: [file],
            validation,
         },
         {root: true}
      );
      await dispatch('loadEmployees', {companyId, periodId});
   },

   /** Load the Employees upload category */
   async loadUploadCategory({commit, dispatch}, {companyId}) {
      const uploadCategory = await dispatch(
         'uploads/loadSpecialCategory',
         {
            companyId,
            category: SpecialCategories.EMPLOYEES,
         },
         {root: true}
      );

      commit('setUploadCategoryId', {
         uploadCategoryId: uploadCategory.id,
      });
   },
};

export default {
   namespaced: true,
   state,
   getters,
   mutations,
   actions,
};
